  import React, { useState, useEffect } from "react";
  import { useNavigate } from "react-router-dom";
  import { postBackend, putBackend, getBackend } from "../../api/backend";
  import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Chip,
    Box,
    Checkbox,
    Typography,
    Snackbar,
    Alert,
  } from "@mui/material";
  import capitalizeFirstLetter from "../../utils/utils";
  import Autocomplete from "@mui/material/Autocomplete";
  import CustomDatePicker from "../CustomDatePicker";
  import { HexColorPicker } from "react-colorful";

  const ItemForm = ({
    item,
    tableName,
    onClose,
    fieldsConfig,
    extraFieldInstances,
    routeAfterCreate,
    action,
  }) => {
    const [itemState, setItemState] = useState(() => {
      let initialState = Object.keys(item).reduce((acc, key) => {
        const field = fieldsConfig.find((f) => f.key === key);
        if (field && field.type === "select") {
          acc[key] = field.options[item[key]] || item[key];
        } else if (field && field.type === "select-multiple") {
          // Convert UUIDs to display values for multiple select fields
          acc[key] = Array.isArray(item[key])
            ? item[key].map((value) => field.options[value] || value)
            : item[key].split(",").map((value) => field.options[value] || value);
        } else {
          acc[key] = item[key];
        }
        return acc;
      }, {});
      return initialState;
    });

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [filteredOptions, setFilteredOptions] = useState({});
    const [extraFields, setExtraFields] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
      // Load initial data into the form state
      const onLoad = async () => {
        for (const field of fieldsConfig) {
          if (field.includeInFormSubmit) {
            setItemState((prevState) => ({
              ...prevState,
              [field.key]: field.value,
            }));
          }
        }
      };
      onLoad();
    }, [fieldsConfig]);

    useEffect(() => {
      setItemState(item);
    }, [item]);

    const handleOnChange = async (key, value) => {
      const fieldConfig = fieldsConfig.find((field) => field.key === key);
      let actualValue = value;

      setItemState((prevState) => {
        const newState = {
          ...prevState,
          [key]: actualValue,
        };

        if (key === "client_name") {
          newState.owner_name = value;
        }

        return newState;
      });

      // Determinar el valor actual según el tipo de campo
      if (fieldConfig?.type === "select-multiple") {
        actualValue = value; // Para múltiples selecciones, value ya es un array
      } else if (fieldConfig?.type === "select") {
        actualValue = value; // Para selección única
      }

      // Manejar campos dependientes
      const dependantField = fieldsConfig.find((field) => field.depends === key);
      if (dependantField) {
        let tempOptions;
        if (fieldConfig?.type === "select-multiple") {
          // Filtrar opciones para campos dependientes en selecciones múltiples
          tempOptions = dependantField.options.filter((option) =>
            actualValue.includes(option[dependantField.depends])
          );
        } else {
          // Filtrar opciones para campos dependientes en selección única
          tempOptions = dependantField.options.filter(
            (option) => option[dependantField.depends] === actualValue
          );
        }

        const tempExtraFields = dependantField.uses.filter((item) =>
          tempOptions.some(
            (option) => option[dependantField.usesKey] === item.uuid
          )
        );

        setExtraFields(
          tempExtraFields.map((field) => ({
            key: field.uuid,
            label: field[dependantField.usesKey],
          }))
        );
      }

      // Manejar opciones filtradas para Autocomplete
      if (
        fieldConfig?.type === "select" ||
        fieldConfig?.type === "select-multiple"
      ) {
        const tempOptions = Object.values(fieldConfig.options) || [];
        let filtered;

        if (Array.isArray(value)) {
          // Para múltiples selecciones, filtrar basado en el último valor ingresado
          const lastValue = value[value.length - 1] || "";
          filtered = tempOptions.filter((option) =>
            option?.toLowerCase().includes(lastValue?.toLowerCase())
          );
        } else {
          // Para selección única
          filtered = tempOptions.filter((option) =>
            option?.toLowerCase().includes(value?.toLowerCase())
          );
        }

        setFilteredOptions((prevState) => ({
          ...prevState,
          [key]: filtered,
        }));
      }
    };

    const getAutocompleteValue = (field) => {
      const value = itemState[field.key];
      if (!value) return field.type === "select-multiple" ? [] : null;
      
      if (field.type === 'select-multiple') {
        return Array.isArray(value) 
          ? value.map(v => field.options[v] || v)
          : [field.options[value] || value];
      } else {
        return field.options[value] || value;
      }
    };

    const validateEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      let requiredFields = [];
    
      fieldsConfig.forEach((field) => {
        if (field.required && field.key === "password" && action === "edit") {
          return;
        }
        if (field.required && (!itemState[field.key]) || (Array.isArray(itemState[field.key]) && itemState[field.key].length === 0 )) {

          requiredFields.push(field.label);
        }
      });
    
      if (requiredFields.length > 0) {
        setSnackbarMessage(
          `Los siguientes campos son obligatorios: ${requiredFields.join(", ")}`
        );
        setOpenSnackbar(true);
        return null;
      }
    

      const submissionData = Object.keys(itemState).reduce((acc, key) => {
        const field = fieldsConfig.find((f) => f.key === key);
        if (field && field.type === "select") {
          const entry = Object.entries(field.options).find(
            ([_, value]) => value === itemState[key]
          );
          acc[key] = entry ? entry[0] : itemState[key];
        } else if (field && field.type === "select-multiple") {
          // Convert back to array of UUIDs for submission
          acc[key] = itemState[key].map((value) => {
            const entry = Object.entries(field.options).find(
              ([_, optionValue]) => optionValue === value
            );
            return entry ? entry[0] : value;
          });
        } else {
          acc[key] = itemState[key];
        }
        return acc;
      }, {});

      if (itemState["email"] && !validateEmail(itemState["email"])) {
        setSnackbarMessage(
          "El e-mail debe tener el siguiente formato: usuario@dominio"
        );
        setOpenSnackbar(true);
        return;
      }
      let response;

      if (Object.keys(item).length > 1) {
        response = await putBackend(tableName, submissionData);
      } else {
        response = await postBackend(tableName, submissionData);
      }

      if (extraFieldInstances) {
        for (const extraFieldInstance of extraFieldInstances) {
          const needs = extraFieldInstance.needs;
          const creates = extraFieldInstance.creates;
          const fieldKey = extraFieldInstance.fieldKey;
          let existingFields = await getBackend(extraFieldInstance.instance);
          existingFields = existingFields.filter(
            (existingField) => existingField[needs] === response.uuid
          );
          for (const extraField of extraFields) {
            const tempExistingField = existingFields.filter(
              (field) => field[creates] === extraField.key
            );
            const body = {
              [needs]: response.uuid,
              [creates]: extraField.key,
              [fieldKey]: "",
            };
            if (tempExistingField.length > 0) {
              body["uuid"] = tempExistingField[0].uuid;
              await putBackend(extraFieldInstance.instance, body);
            } else {
              await postBackend(extraFieldInstance.instance, body);
            }
          }
        }
      }

      if (routeAfterCreate) {
        navigate(routeAfterCreate);
      }
      onClose();
    };

    return (
      <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
        <DialogTitle>
          {Object.keys(item).length > 1 ? "Editar entrada" : "Añadir entrada"}
        </DialogTitle>
        <DialogContent>
          {fieldsConfig.map(
            (field) =>
              !field.depends &&
              !field.includeInFormSubmit &&
              field.displayForm &&
              (field.type === "select" || field.type === "select-multiple" ? (
                <Autocomplete
                  key={field.key}
                  multiple={field.type === "select-multiple"}
                  id={`autocomplete-${field.key}`}
                  options={Object.values(field.options)}

                  value={getAutocompleteValue(field)}
                  onChange={(e, newValue) => handleOnChange(field.key, newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      variant="outlined"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  sx={{ marginTop: "12px" }}
                />
              ) : field.type === "date" ? (
                <CustomDatePicker
                  key={field.key}
                  displayText={capitalizeFirstLetter(field.label)}
                  value={itemState?.[field.key]}
                  onChange={(newValue) => handleOnChange(field.key, newValue)}
                  sx={{ marginTop: "12px" }}
                />
              ) : field.type === "color-picker" ? (
                <div key={field.key}>
                  <Typography variant="h6" textAlign="start">
                    {capitalizeFirstLetter(field.label)}
                  </Typography>
                  <HexColorPicker
                    color={itemState?.[field.key]}
                    onChange={(color) => handleOnChange(field.key, color)}
                  />
                </div>
              ) : field.type === "bool" ? (
                <Box
                  key={field.key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "12px",
                  }}
                >
                  <Typography variant="h6" textAlign="start">
                    {capitalizeFirstLetter(field.label)}
                  </Typography>
                  <Checkbox
                    textAlign="flex-end"
                    checked={itemState?.[field.key]}
                    onChange={(e) => handleOnChange(field.key, e.target.checked)}
                  />
                </Box>
              ) : (
                <TextField
                  key={field.key}
                  margin="dense"
                  label={capitalizeFirstLetter(field.label)}
                  type={field.type}
                  fullWidth
                  value={itemState?.[field.key] || ""}
                  onChange={(e) => handleOnChange(field.key, e.target.value)}
                  sx={{ marginTop: "12px" }}
                />
              ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} color="primary">
            {Object.keys(item).length !== 0 ? "Actualizar" : "Agregar"}
          </Button>
        </DialogActions>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity="warning">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  };

  export default ItemForm;
